import { useEffect, useState } from 'react';
import { FiTrash2, FiLock, FiInfo, FiUserX, FiCheckCircle } from 'react-icons/fi';
import { apiRequest } from '../utils/auth';
import Pagination from '../components/Pagination';

interface DeletionRequest {
  _id: string;
  userId: string;
  reason: string;
  requestedAt: string;
  scheduledDeletionDate: string;
  status: 'pending' | 'completed' | 'cancelled';
  processedAt?: string;
  userEmail?: string;
  userName?: string;
  userExists: boolean;
}

interface PaginationState {
  total: number;
  limit: number;
  skip: number;
}

export default function AccountDeletionsPage() {
  const [requests, setRequests] = useState<DeletionRequest[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [pagination, setPagination] = useState<PaginationState>({ total: 0, limit: 20, skip: 0 });
  const [statusFilter, setStatusFilter] = useState('pending');
  const [actionLoading, setActionLoading] = useState<string | null>(null);
  const [statusCounts, setStatusCounts] = useState<Record<string, number>>({});

  const STATUS_TABS = [
    { key: '', label: 'All' },
    { key: 'pending', label: 'Pending' },
    { key: 'completed', label: 'Completed' },
    { key: 'cancelled', label: 'Cancelled' },
  ];

  const fetchStatusCounts = async () => {
    try {
      const response = await apiRequest<{ success: boolean; counts: Record<string, number> }>('/api/admin/account-deletions/status-counts');
      setStatusCounts(response.counts);
    } catch {
      // non-critical, tabs just won't show counts
    }
  };

  const fetchRequests = async (skip = 0) => {
    try {
      setLoading(true);
      setError(null);
      const params = new URLSearchParams({
        limit: '20',
        skip: skip.toString(),
        ...(statusFilter && { status: statusFilter }),
      });
      const response = await apiRequest<{
        success: boolean;
        requests: DeletionRequest[];
        total: number;
        limit: number;
        skip: number;
      }>(`/api/admin/account-deletions?${params}`);
      setRequests(response.requests);
      setPagination({ total: response.total, limit: response.limit, skip: response.skip });
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Failed to fetch deletion requests');
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchRequests(0);
    fetchStatusCounts();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [statusFilter]);

  const handleDelete = async (req: DeletionRequest) => {
    const label = req.userEmail || req.userName || req.userId;
    if (!window.confirm(
      `Permanently delete the account for "${label}"?\n\n` +
      `This deletes the user record and purges their image files from storage. This action cannot be undone.`,
    )) return;
    setActionLoading(req._id);
    try {
      await apiRequest(`/api/admin/account-deletions/${req._id}/delete`, { method: 'POST' });
      fetchRequests(pagination.skip);
      fetchStatusCounts();
    } catch (err) {
      alert(err instanceof Error ? err.message : 'Failed to delete account');
    } finally {
      setActionLoading(null);
    }
  };

  const getStatusBadge = (status: string) => {
    const colors: Record<string, { bg: string; color: string }> = {
      pending: { bg: 'var(--color-warning-strong)', color: '#fff' },
      completed: { bg: 'var(--color-success-strong)', color: '#fff' },
      cancelled: { bg: 'var(--color-subtle)', color: '#fff' },
    };
    const c = colors[status] || { bg: 'var(--color-subtle)', color: '#fff' };
    return (
      <span style={{ padding: '4px 8px', borderRadius: '4px', backgroundColor: c.bg, color: c.color, fontSize: '12px', fontWeight: 500 }}>
        {status.charAt(0).toUpperCase() + status.slice(1)}
      </span>
    );
  };

  const formatDate = (d: string) =>
    new Date(d).toLocaleDateString('en-US', { month: 'short', day: 'numeric', year: 'numeric', hour: '2-digit', minute: '2-digit' });

  // Whole days remaining until the grace window ends (>= 0). Returns 0 once the
  // scheduled date has passed, so the row becomes actionable.
  const daysUntil = (d: string) => {
    const ms = new Date(d).getTime() - Date.now();
    return ms <= 0 ? 0 : Math.ceil(ms / (1000 * 60 * 60 * 24));
  };

  const isWindowPassed = (d: string) => new Date(d).getTime() <= Date.now();

  const renderAction = (req: DeletionRequest) => {
    if (req.status === 'cancelled') {
      return <span style={{ fontSize: '12px', color: 'var(--color-muted)' }}>Cancelled by user</span>;
    }
    if (req.status === 'completed') {
      return (
        <span style={{ fontSize: '12px', color: 'var(--color-success)', display: 'inline-flex', alignItems: 'center', gap: '4px' }}>
          <FiCheckCircle size={13} /> Deleted{req.processedAt ? ` · ${formatDate(req.processedAt)}` : ''}
        </span>
      );
    }
    // pending
    if (!isWindowPassed(req.scheduledDeletionDate)) {
      const days = daysUntil(req.scheduledDeletionDate);
      return (
        <span
          style={{ fontSize: '12px', color: 'var(--color-muted)', display: 'inline-flex', alignItems: 'center', gap: '4px' }}
          title={`Read-only until the grace window ends on ${formatDate(req.scheduledDeletionDate)}`}
        >
          <FiLock size={13} /> Locked · {days} day{days === 1 ? '' : 's'} left
        </span>
      );
    }
    return (
      <button
        onClick={() => handleDelete(req)}
        disabled={actionLoading === req._id}
        style={{ padding: '4px 12px', backgroundColor: 'var(--color-danger-strong)', color: 'white', border: 'none', borderRadius: '4px', cursor: 'pointer', fontSize: '12px', display: 'inline-flex', alignItems: 'center', gap: '4px', opacity: actionLoading === req._id ? 0.6 : 1 }}
      >
        <FiTrash2 size={13} /> {actionLoading === req._id ? 'Deleting…' : 'Delete'}
      </button>
    );
  };

  const totalPages = Math.ceil(pagination.total / pagination.limit);
  const currentPage = Math.floor(pagination.skip / pagination.limit) + 1;

  return (
    <div className="page">
      <div style={{ marginBottom: '12px', display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
        <h1 style={{ margin: 0, fontSize: '24px', fontWeight: 'bold' }}>Account Deletion Requests</h1>
        <button
          onClick={() => { fetchRequests(0); fetchStatusCounts(); }}
          style={{ padding: '8px 16px', backgroundColor: 'var(--color-success-strong)', color: 'white', border: 'none', borderRadius: '4px', cursor: 'pointer', fontSize: '14px' }}
        >
          Refresh
        </button>
      </div>

      <div style={{ marginBottom: '20px', padding: '12px 14px', backgroundColor: 'var(--color-surface-secondary)', border: '1px solid var(--color-border)', borderRadius: '6px', color: 'var(--color-muted)', fontSize: '13px', display: 'flex', gap: '8px', alignItems: 'flex-start' }}>
        <FiInfo size={16} style={{ marginTop: '1px', flexShrink: 0 }} />
        <span>
          Requests are <strong style={{ color: 'var(--color-text)' }}>read-only during the 30-day grace window</strong> — the user can still log back in and cancel. Once the scheduled date passes, a <strong style={{ color: 'var(--color-danger)' }}>Delete</strong> button appears; deleting removes the user record and purges their stored image files. This cannot be undone.
        </span>
      </div>

      {/* Status Tabs */}
      <div style={{ display: 'flex', gap: '4px', marginBottom: '20px', borderBottom: '1px solid var(--color-border)', overflowX: 'auto' }}>
        {STATUS_TABS.map((tab) => {
          const isActive = statusFilter === tab.key;
          const count = tab.key === '' ? (statusCounts.all ?? 0) : (statusCounts[tab.key] ?? 0);
          return (
            <button
              key={tab.key}
              onClick={() => setStatusFilter(tab.key)}
              style={{
                padding: '10px 16px',
                border: 'none',
                borderBottom: isActive ? '2px solid var(--color-accent)' : '2px solid transparent',
                backgroundColor: 'transparent',
                color: isActive ? 'var(--color-text)' : 'var(--color-muted)',
                fontWeight: isActive ? 600 : 400,
                cursor: 'pointer',
                fontSize: '14px',
                whiteSpace: 'nowrap',
                transition: 'color 0.15s, border-color 0.15s',
              }}
            >
              {tab.label} ({count})
            </button>
          );
        })}
      </div>

      {error && (
        <div style={{ padding: '12px', backgroundColor: 'var(--color-danger-surface)', color: 'var(--color-danger)', borderRadius: '4px', marginBottom: '20px' }}>
          {error}
        </div>
      )}

      {loading ? (
        <div style={{ padding: '40px', textAlign: 'center', color: 'var(--color-muted)' }}>Loading requests...</div>
      ) : requests.length === 0 ? (
        <div style={{ padding: '40px', textAlign: 'center', color: 'var(--color-muted)' }}>
          <FiUserX size={48} style={{ marginBottom: '12px', opacity: 0.5 }} />
          <p>No account deletion requests found</p>
        </div>
      ) : (
        <>
          <div style={{ marginBottom: '12px', color: 'var(--color-muted)', fontSize: '14px' }}>
            Showing {requests.length} of {pagination.total} requests
          </div>
          <div style={{ backgroundColor: 'var(--color-surface)', borderRadius: '8px', overflow: 'hidden', boxShadow: '0 1px 3px rgba(0,0,0,0.3)' }}>
            <table style={{ width: '100%', borderCollapse: 'collapse' }}>
              <thead style={{ backgroundColor: 'var(--color-surface-hover)' }}>
                <tr>
                  <th style={{ padding: '12px', textAlign: 'left', fontWeight: 600, borderBottom: '2px solid var(--color-border)', color: 'var(--color-text)' }}>User</th>
                  <th style={{ padding: '12px', textAlign: 'left', fontWeight: 600, borderBottom: '2px solid var(--color-border)', color: 'var(--color-text)' }}>Reason</th>
                  <th style={{ padding: '12px', textAlign: 'left', fontWeight: 600, borderBottom: '2px solid var(--color-border)', color: 'var(--color-text)' }}>Requested</th>
                  <th style={{ padding: '12px', textAlign: 'left', fontWeight: 600, borderBottom: '2px solid var(--color-border)', color: 'var(--color-text)' }}>Scheduled Deletion</th>
                  <th style={{ padding: '12px', textAlign: 'center', fontWeight: 600, borderBottom: '2px solid var(--color-border)', color: 'var(--color-text)' }}>Status</th>
                  <th style={{ padding: '12px', textAlign: 'center', fontWeight: 600, borderBottom: '2px solid var(--color-border)', color: 'var(--color-text)' }}>Action</th>
                </tr>
              </thead>
              <tbody>
                {requests.map((req) => (
                  <tr key={req._id} style={{ borderBottom: '1px solid var(--color-border)' }}>
                    <td style={{ padding: '12px', fontSize: '13px', color: 'var(--color-text)' }} title={req.userId}>
                      <div>{req.userEmail || req.userName || req.userId.slice(0, 14) + '…'}</div>
                      {req.userName && req.userEmail && (
                        <div style={{ fontSize: '12px', color: 'var(--color-muted)' }}>{req.userName}</div>
                      )}
                      {!req.userExists && (
                        <div style={{ fontSize: '11px', color: 'var(--color-muted)', fontStyle: 'italic' }}>user record gone</div>
                      )}
                    </td>
                    <td style={{ padding: '12px', fontSize: '13px', color: 'var(--color-text-secondary)', maxWidth: '260px' }}>{req.reason}</td>
                    <td style={{ padding: '12px', fontSize: '13px', color: 'var(--color-muted)' }}>{formatDate(req.requestedAt)}</td>
                    <td style={{ padding: '12px', fontSize: '13px', color: 'var(--color-muted)' }}>{formatDate(req.scheduledDeletionDate)}</td>
                    <td style={{ padding: '12px', textAlign: 'center' }}>{getStatusBadge(req.status)}</td>
                    <td style={{ padding: '12px', textAlign: 'center' }}>{renderAction(req)}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>

          <Pagination
            currentPage={currentPage}
            totalPages={totalPages}
            onPageChange={(page) => fetchRequests((page - 1) * pagination.limit)}
          />
        </>
      )}
    </div>
  );
}
